// TaskDetailDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import ReactMarkdown from 'react-markdown';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { 
  ListTodo, Bot, Brain, Lightbulb, Cpu, ChevronDown, RefreshCw, 
  AlertTriangle, Image as ImageIcon 
} from 'lucide-react';

/**
 * Dialog for viewing task details with AI reasoning breakdown
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.taskDetail - Task detail data with AI reasoning
 * @param {boolean} props.loading - Whether task detail is loading
 * @param {function} props.onViewDeliverable - Callback when a deliverable is clicked
 * @param {function} props.onClose - Callback when close is clicked
 */
export const TaskDetailDialog = ({
  open,
  onOpenChange,
  taskDetail,
  loading = false,
  onViewDeliverable,
  onClose
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-blue-500" />
            Task Details
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4">
            {taskDetail?.agent?.name && (
              <span className="flex items-center gap-1">
                <Bot className="h-4 w-4" />
                {taskDetail.agent.name}
              </span>
            )}
            <Badge className={
              taskDetail?.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
              taskDetail?.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
              taskDetail?.status === 'failed' ? 'bg-red-100 text-red-700' :
              'bg-slate-100 text-slate-700'
            }>
              {taskDetail?.status || 'pending'}
            </Badge>
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : taskDetail ? (
          <div className="space-y-6 py-4">
            {/* Task Info */}
            <div>
              <h3 className="font-semibold text-lg">{taskDetail.title}</h3>
              {taskDetail.description && (
                <p className="text-sm text-muted-foreground mt-1">{taskDetail.description}</p>
              )}
            </div>
            
            {/* Agent AI Config */}
            {taskDetail.agent?.ai_config && (
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-blue-50 border-blue-200">
                  <Brain className="h-3 w-3 mr-1" />
                  Left: {taskDetail.agent.ai_config.left_brain_model || 'GPT-4o'}
                </Badge>
                <Badge variant="outline" className="bg-pink-50 border-pink-200">
                  <Lightbulb className="h-3 w-3 mr-1" />
                  Right: {taskDetail.agent.ai_config.right_brain_model || 'Claude 4.5'}
                </Badge>
                <Badge variant="outline" className="bg-purple-50 border-purple-200">
                  <Cpu className="h-3 w-3 mr-1" />
                  Cortex: {taskDetail.agent.ai_config.brain_stem_model || 'Gemini Pro'}
                </Badge>
              </div>
            )}
            
            {/* AI Reasoning Breakdown */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Brain className="h-4 w-4" />
                AI Reasoning Breakdown
              </h4>
              
              {/* Left Brain (Analytical) */}
              <Collapsible defaultOpen={!!taskDetail.ai_reasoning?.left_brain?.output}>
                <Card className="border-blue-200">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 cursor-pointer hover:bg-blue-50 transition-colors">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                          Left Brain (Analytical)
                          <Badge variant="outline" className="text-xs">{taskDetail.ai_reasoning?.left_brain?.model || 'GPT-4o'}</Badge>
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      {taskDetail.ai_reasoning?.left_brain?.output ? (
                        <div className="bg-blue-50 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown>{taskDetail.ai_reasoning.left_brain.output}</ReactMarkdown>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No analytical output recorded</p>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
              
              {/* Right Brain (Creative) */}
              <Collapsible defaultOpen={!!taskDetail.ai_reasoning?.right_brain?.output}>
                <Card className="border-pink-200">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 cursor-pointer hover:bg-pink-50 transition-colors">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-pink-500"></div>
                          Right Brain (Creative)
                          <Badge variant="outline" className="text-xs">{taskDetail.ai_reasoning?.right_brain?.model || 'Claude 4.5'}</Badge>
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      {taskDetail.ai_reasoning?.right_brain?.output ? (
                        <div className="bg-pink-50 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown>{taskDetail.ai_reasoning.right_brain.output}</ReactMarkdown>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No creative output recorded</p>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
              
              {/* Cortex (Synthesis) */}
              <Collapsible defaultOpen={!!taskDetail.ai_reasoning?.cortex?.output}>
                <Card className="border-purple-200">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 cursor-pointer hover:bg-purple-50 transition-colors">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-purple-500"></div>
                          Cortex (Synthesis)
                          <Badge variant="outline" className="text-xs">{taskDetail.ai_reasoning?.cortex?.model || 'Gemini Pro'}</Badge>
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      {taskDetail.ai_reasoning?.cortex?.output ? (
                        <div className="bg-purple-50 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown>{taskDetail.ai_reasoning.cortex.output}</ReactMarkdown>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No synthesis output recorded</p>
                      )}
                      
                      {/* Execution Pathway */}
                      {taskDetail.ai_reasoning?.cortex?.execution_pathway && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium mb-2">Execution Pathway</h5>
                          <div className="bg-white rounded-lg p-3 border">
                            <pre className="text-xs whitespace-pre-wrap">
                              {JSON.stringify(taskDetail.ai_reasoning.cortex.execution_pathway, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            </div>
            
            {/* Deliverables */}
            {taskDetail.deliverables?.length > 0 && (
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <ImageIcon className="h-4 w-4" />
                  Deliverables ({taskDetail.deliverables.length})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {taskDetail.deliverables.map((del, idx) => (
                    <Card 
                      key={idx} 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => onViewDeliverable && onViewDeliverable({
                        ...del,
                        id: `${taskDetail.task_id}_${idx}`,
                        task_id: taskDetail.task_id,
                        agent_name: taskDetail.agent?.name
                      })}
                      data-testid={`task-deliverable-${idx}`}
                    >
                      <CardContent className="p-3">
                        <Badge className={
                          del.type === 'image' ? 'bg-pink-100 text-pink-700' :
                          del.type === 'document' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {del.type}
                        </Badge>
                        {del.type === 'image' && del.base64 ? (
                          <div className="aspect-video bg-slate-100 rounded mt-2 overflow-hidden">
                            <img 
                              src={`data:image/png;base64,${del.base64}`}
                              alt="Deliverable"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                            {del.content?.substring(0, 100) || 'No preview'}...
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No task details available</p>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="task-detail-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailDialog;
